import Modal from 'mini-modal/lib/components/ModalPortal';
import React, { useEffect, useRef } from 'react';
import { isNullOrUndefined } from 'mini-common-utils/lib/utils/isNullOrUndefined';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';

const styles = require('./InfoModal.scss');

type ModalComponentElements = 'Media' | 'Title' | 'Subtitle' | 'ActionButton';

interface InfoModalChildProps {
    type?: ModalComponentElements;
}

interface ActionButtonProps extends InfoModalChildProps {
    onClick: () => void;
    text: string;
    ariaLabel?: string;
}

interface VideoSrc {
    src: string;
    type: string;
}

interface MediaProps extends InfoModalChildProps {
    videoSrc?: VideoSrc[];
    imageSrc?: string;
}

interface InfoModalChildren<P> extends React.FC<P> {
    Title: React.FC<InfoModalChildProps>;
    Media?: React.FC<MediaProps>;
    Subtitle?: React.FC<InfoModalChildProps>;
    ActionButton?: React.FC<ActionButtonProps>;
}

interface InfoModalProps {
    elementToFocusOnClose?: React.RefObject<any>;
    onOverlayClick?: () => void;
}

/**
 * The Media slot
 * @param props
 */
const Media: React.FC<MediaProps> = props =>
    props.videoSrc ? (
        <video className={styles.media} autoPlay loop playsInline muted poster={props.imageSrc}>
            {props.videoSrc.map(video => (
                <source src={video.src} type={video.type} />
            ))}
        </video>
    ) : (
        <img className={styles.media} src={props.imageSrc} alt="" role="presentation" />
    );
Media.defaultProps = {
    type: 'Media',
};

/**
 * The Title slot
 * @param props
 */
const Title: React.FC<InfoModalChildProps> = props => {
    const titleRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        window.requestAnimationFrame(() => trySetFocusOnElement(titleRef));
    });
    return (
        <div className={styles.title} ref={titleRef} tabIndex={0} role="heading" aria-level={1}>
            {props.children}
        </div>
    );
};
Title.defaultProps = {
    type: 'Title',
};

/**
 * The Subtitle slot
 * @param props
 */
const Subtitle: React.FC<InfoModalChildProps> = props => (
    <div className={styles.subtitle} tabIndex={0}>
        {props.children}
    </div>
);
Subtitle.defaultProps = {
    type: 'Subtitle',
};

/**
 * The Action button slot
 * @param props
 */
const ActionButton: React.FC<ActionButtonProps> = props =>
    props.children ? (
        <div role="button" onClick={props.onClick} tabIndex={0} aria-label={props.ariaLabel}>
            {props.children}
        </div>
    ) : (
        <button
            role="button"
            onClick={props.onClick}
            tabIndex={0}
            aria-label={props.ariaLabel}
            className={styles.actionButton}>
            {props.text}
        </button>
    );
ActionButton.defaultProps = {
    type: 'ActionButton',
    ariaLabel: '',
    text: 'Ok',
};

/**
 * Component which renders the modal
 * @param props
 */
const InfoModal: InfoModalChildren<InfoModalProps> = props => {
    const { children, onOverlayClick, elementToFocusOnClose } = props;

    if (!children) {
        return null;
    }
    const modalChildren = {
        Media: undefined,
        Title: undefined,
        Subtitle: undefined,
        ActionButton: undefined,
    };
    React.Children.forEach(children, child => {
        if (React.isValidElement(child) && !!child.props && !isNullOrUndefined(child.props.type)) {
            modalChildren[child.props.type] = child;
        } else {
            throw new Error(
                'Children of the Modal should have type property with values Media | Title | Subtitle | ActionButton'
            );
        }
    });
    const { Media, Title, Subtitle, ActionButton } = modalChildren;
    return !(Media || Title || Subtitle || ActionButton) ? null : (
        <Modal onOverlayClick={onOverlayClick} elementToFocusOnClose={elementToFocusOnClose}>
            <div className={styles.infoModalContent}>
                {Media}
                {Title}
                {Subtitle}
                {ActionButton}
            </div>
        </Modal>
    );
};

InfoModal.Media = Media;
InfoModal.Title = Title;
InfoModal.Subtitle = Subtitle;
InfoModal.ActionButton = ActionButton;

export {
    InfoModal,
    Media,
    Title,
    Subtitle,
    ActionButton,
    MediaProps,
    ActionButtonProps,
    InfoModalChildProps,
    InfoModalProps,
    VideoSrc,
};
